'use client'

import type { ComponentProps } from 'react'

import { CaretLeftIcon, CaretRightIcon } from '@/components/icons/icon-set'
import { cn } from '@/lib/utils'

/*
 * GoodUnited / Pagination. Figma: 10 / Components / Table (Pagination).
 * 24px page buttons, the current page fills with Colors/action/primary, Roboto / text-xs.
 * Previous and next are 24px outline buttons.
 */
interface PaginationProps extends Omit<ComponentProps<'nav'>, 'onChange'> {
    page: number
    pageCount: number
    onPageChange: (page: number) => void
    siblings?: number
}

const pageItems = (page: number, pageCount: number, siblings: number): Array<number | 'gap'> => {
    const first = 1
    const last = pageCount
    const start = Math.max(first, Math.min(page - siblings, last - siblings * 2 - 1))
    const end = Math.min(last, Math.max(page + siblings, first + siblings * 2 + 1))
    const items: Array<number | 'gap'> = []

    for (let value = start; value <= end; value += 1) items.push(value)
    if (start > first) {
        if (start > first + 1) items.unshift('gap')
        items.unshift(first)
    }
    if (end < last) {
        if (end < last - 1) items.push('gap')
        items.push(last)
    }

    return items
}

const pageButton = 'inline-flex h-6 min-w-6 cursor-pointer items-center justify-center rounded-sm px-1 text-xs font-medium text-text-primary transition-colors outline-none hover:bg-action-secondary-hover focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-border-focus'
const stepButton = 'inline-flex size-6 cursor-pointer items-center justify-center rounded-sm border border-action-secondary-border bg-action-secondary text-icon-primary transition-colors outline-none hover:bg-action-secondary-hover focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-border-focus disabled:cursor-not-allowed disabled:text-icon-disabled [&_svg]:size-3.5'

function Pagination({ page, pageCount, onPageChange, siblings = 2, className, ...props }: PaginationProps) {
    const items = pageItems(page, Math.max(pageCount, 1), siblings)

    return (
        <nav data-slot='pagination' aria-label='Pagination' className={cn('flex items-center gap-1', className)} {...props}>
            <button type='button' className={stepButton} onClick={() => onPageChange(page - 1)} disabled={page <= 1} aria-label='Previous page'>
                <CaretLeftIcon aria-hidden='true' />
            </button>
            {items.map((item, index) => item === 'gap'
                ? (
                    <span key={`gap-${index}`} className='inline-flex h-6 min-w-6 items-center justify-center text-xs text-text-secondary' aria-hidden='true'>
                        ...
                    </span>
                )
                : (
                    <button
                        key={item}
                        type='button'
                        onClick={() => onPageChange(item)}
                        aria-current={item === page ? 'page' : undefined}
                        aria-label={`Page ${item}`}
                        className={cn(pageButton, item === page && 'bg-action-primary text-action-primary-foreground hover:bg-action-primary')}
                    >
                        {item.toLocaleString('en-US')}
                    </button>
                ))}
            <button type='button' className={stepButton} onClick={() => onPageChange(page + 1)} disabled={page >= pageCount} aria-label='Next page'>
                <CaretRightIcon aria-hidden='true' />
            </button>
        </nav>
    )
}

export { Pagination }
